<aside
  *transloco="let t"
  [class]="isSideBarOpen ? 'lg:w-62 xs:h-96' : 'lg:w-24 xs:h-[6.2rem]'"
  class="relative lg:fixed lg:flex lg:flex-col lg:h-full xs:w-full duration-400 text-white bg-primary transition-all z-10 overflow-hidden lg:overflow-visible"
  style="transition-timing-function: cubic-bezier(0.34, 1.56, 0.64, 1)"
>
  <div class="flex flex-col flex-1 items-center gap-8 px-4 pt-9 pb-3 min-h-0">
    <div class="flex flex-col items-center gap-6 w-full shrink-0">
      <!-- Desktop logo: crossfade between full and icon-only — height fixed so nav never shifts -->
      <div
        class="relative hidden lg:flex items-center justify-center overflow-hidden h-17.5"
        [style.width.px]="isSideBarOpen ? 180 : 48"
        style="transition: width 400ms cubic-bezier(0.34, 1.56, 0.64, 1)"
      >
        <img
          alt="juno logo"
          class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-200"
          [class]="isSideBarOpen ? 'opacity-100 delay-150' : 'opacity-0'"
          ngSrc="/images/junoAllWhite-Logo.svg"
          height="70"
          width="180"
          priority="true"
        />
        <img
          alt="juno logo"
          class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-200"
          [class]="isSideBarOpen ? 'opacity-0' : 'opacity-100 delay-150'"
          ngSrc="/images/junologo-white.svg"
          height="50"
          width="48"
          priority="true"
        />
      </div>

      <!-- Mobile logo: crossfade — dynamic height so it fits the collapsed bar -->
      <div class="relative lg:hidden w-full flex items-center justify-center overflow-hidden" [style.height.px]="44">
        <img
          alt="juno logo"
          class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-200 opacity-100 delay-150"
          ngSrc="/images/JunoLogoSidebar.svg"
          height="44"
          width="160"
          priority="true"
        />
      </div>
    </div>

    <nav class="w-full shrink-0">
      <ul class="flex flex-col gap-2" [class]="isSideBarOpen ? 'items-start' : 'items-center'">
        @for (item of navItems(); track item.route) {
        <li
          *dssLibsUiAuthorizedRole="item.requiredRole"
          routerLinkActive="active"
          #activeLink="routerLinkActive"
          [routerLink]="[item.route]"
          (click)="collapseSidebarOnMenuItemClick()"
          (keydown.enter)="navigateToRoute(item.route)"
          tabindex="0"
          [class]="!isSideBarOpen ? 'xs:hidden lg:flex w-11 justify-center' : 'w-full px-3 gap-2'"
          class="group relative flex items-center h-11 rounded-lg text-white/60 hover:bg-white/8 hover:text-white transition-all duration-300 cursor-pointer list-none"
        >
          <mat-icon
            class="shrink-0 transition-all duration-300"
            [class]="isSideBarOpen ? 'size-4.5!' : 'size-5'"
            [svgIcon]="activeLink.isActive ? item.activeIcon : item.inactiveIcon"
          ></mat-icon>

          <!-- Label: always in DOM — fades and slides, quick out / delayed in -->
          <span
            style="transition-timing-function: cubic-bezier(0.34, 1.56, 0.64, 1)"
            [class]="
              [
                'whitespace-nowrap overflow-hidden transition-[opacity,max-width,transform]',
                isSideBarOpen
                  ? 'opacity-100 max-w-48 translate-x-0 duration-200 delay-150'
                  : 'opacity-0 max-w-0 -translate-x-2 duration-150',
                activeLink.isActive ? 'text-sm font-semibold' : 'text-sm font-medium'
              ].join(' ')
            "
            >{{ t(item.labelKey) }}</span
          >

          <!-- Tooltip: opacity fade on hover when collapsed -->
          <span
            class="nav-tooltip absolute top-1/2 -translate-y-1/2 inset-s-12 bg-primary py-1 px-2 rounded whitespace-nowrap text-sm text-white z-20 pointer-events-none transition-opacity duration-150"
            [class]="isSideBarOpen ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'"
          >
            {{ t(item.labelKey) }}
          </span>
        </li>
        }
      </ul>
    </nav>

    <!-- Spacer -->
    <div class="flex-1 min-h-0 hidden lg:block"></div>

    <!-- User section: min-h reserves the full open-state height so the avatar never drifts -->
    <div class="w-full shrink-0 min-h-35" [class]="!isSideBarOpen ? 'hidden lg:block' : ''">
      <!-- Avatar row -->
      <div
        class="group relative flex items-center py-2 w-full transition-all duration-300"
        [class]="isSideBarOpen ? 'gap-2' : 'justify-center'"
      >
        <button
          type="button"
          class="shrink-0 flex items-center justify-center w-11 h-11 rounded-full bg-white text-primary text-sm font-semibold shadow-sm ltr:font-Audiowide cursor-pointer overflow-hidden"
          [routerLink]="['/settings/account-info']"
          (click)="collapseSidebarOnMenuItemClick()"
        >
          @if (profilePictureUrl()) {
          <img [src]="profilePictureUrl()" alt="" class="w-full h-full object-cover" />
          } @else {
          {{ userName() | initials }}
          }
        </button>

        <!-- User info: always in DOM — fades in after sidebar expands -->
        <div
          style="transition-timing-function: cubic-bezier(0.34, 1.56, 0.64, 1)"
          class="min-w-0 flex flex-col gap-1 overflow-hidden transition-[opacity,max-width]"
          [attr.inert]="isSideBarOpen ? null : ''"
          [class]="
            isSideBarOpen
              ? 'flex-1 opacity-100 max-w-full duration-200 delay-150 pointer-events-auto'
              : 'opacity-0 max-w-0 duration-150 pointer-events-none'
          "
        >
          <p class="text-sm font-semibold capitalize leading-tight truncate text-white">{{ userName() }}</p>
          <button type="button" class="flex items-center justify-between gap-1 min-w-0" (click)="toggleClientsModal()">
            @if (clientData(); as clientData) {
            <p class="text-xs text-white/60 capitalize leading-tight truncate">{{ clientData.name }}</p>
            }
            <mat-icon class="size-4! cursor-pointer text-white/60" svgIcon="horizontal-transfer-outline"></mat-icon>
          </button>
        </div>

        <!-- Profile tooltip when collapsed -->
        <span
          class="absolute top-1/2 -translate-y-1/2 inset-s-12 bg-primary py-1 px-2 rounded whitespace-nowrap text-sm text-white z-20 pointer-events-none transition-opacity duration-150"
          [class]="isSideBarOpen ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'"
        >
          {{ t('SIDE_BAR.PROFILE') }}
        </span>
      </div>

      <!-- Logout: below avatar, collapses toward bottom -->
      <div
        class="overflow-hidden transition-[max-height,opacity] ease-in-out duration-300"
        [attr.inert]="isSideBarOpen ? null : ''"
        [class]="isSideBarOpen ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'"
      >
        <div class="mt-3 pt-3 border-t border-white/20">
          <button
            type="button"
            (click)="Logout()"
            class="flex items-center h-11 w-full px-3 gap-2 text-white/60 hover:bg-white/8 hover:text-white cursor-pointer rounded-lg transition-colors duration-200"
          >
            <mat-icon class="size-4.5! shrink-0" svgIcon="logout-2-outline"></mat-icon>
            <span class="text-sm font-medium">{{ t('SIDE_BAR.LOG_OUT') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Desktop collapse toggle -->
  <button
    type="button"
    (click)="changeLayout()"
    [class]="isSideBarOpen ? 'inset-s-57.5' : 'inset-s-19.5'"
    style="transition-timing-function: cubic-bezier(0.34, 1.56, 0.64, 1)"
    class="absolute bottom-15 hidden lg:flex items-center justify-center w-9 h-9 rounded-xl bg-white border border-primary shadow-lg cursor-pointer transition-all duration-300"
  >
    <mat-icon
      class="size-4! text-primary transition-transform duration-300"
      [class]="isSideBarOpen ? 'rotate-180 rtl:rotate-0' : 'rtl:rotate-180'"
      svgIcon="arrow-bar-right"
    ></mat-icon>
  </button>

  <!-- Mobile menu toggle -->
  <button
    type="button"
    (click)="changePhoneLayout()"
    class="absolute duration-300 cursor-pointer top-11 inset-s-5 lg:hidden"
  >
    @if (isSideBarOpen) {
    <mat-icon class="size-5!" svgIcon="close-menu"></mat-icon>
    } @else {
    <mat-icon class="size-6!" svgIcon="bar-menu"></mat-icon>
    }
  </button>
</aside>
